import React from "react";
import MarketingShell from "../components/MarketingShell";
import PageMeta from "../components/PageMeta";

/**
 * Terms of Service — pragmatic, plain-English contract for an Indian SaaS.
 * Substitute the [TO FILL] placeholders with your legal entity details.
 */
export default function Terms() {
  return (
    <MarketingShell>
      <PageMeta
        title="Terms of Service · HeDesk"
        description="The terms that govern your use of HeDesk — what you can do, what we can do, payment, refunds, and limits of liability."
        canonical="https://hedesk.help/terms"
      />
      <article className="prose-content">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold">Legal</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
          Terms of Service
        </h1>
        <p className="mt-2 text-sm text-slate-400">Last updated: 14 June 2026</p>

        <Lead>
          By creating a HeDesk account or by using any part of the HeDesk service (the &ldquo;Service&rdquo;), you agree to
          these Terms. If you do not agree, please do not use HeDesk.
        </Lead>

        <H2>1. The agreement</H2>
        <P>
          These Terms form a binding contract between you (or the legal entity you represent) and{" "}
          <strong> hemba. </strong> (&ldquo;HeDesk&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;).
        </P>

        <H2>2. The service</H2>
        <P>
          HeDesk is a subscription-based clinic management platform. We provide tooling to manage appointments,
          patients, prescriptions, billing, online booking, and reminders. We do <strong>not</strong> provide
          medical advice, diagnosis, or treatment. Clinical decisions remain entirely with the licensed
          medical professionals using the platform.
        </P>

        <H2>3. Your account</H2>
        <Ul>
          <li>You must be at least 18 years old and a registered medical professional, clinic operator, or authorised employee thereof.</li>
          <li>You are responsible for keeping login credentials confidential and for all activity under your account.</li>
          <li>You must notify us at <a href="mailto:security@hedesk.help" className="text-blue-400">security@hedesk.help</a> within 24 hours of any suspected unauthorised access.</li>
        </Ul>

        <H2>4. Acceptable use</H2>
        <P>You agree not to:</P>
        <Ul>
          <li>Use the Service for any unlawful purpose or in violation of any law applicable in India.</li>
          <li>Upload patient data without the patient&rsquo;s informed consent under applicable medical-record regulations.</li>
          <li>Reverse-engineer, decompile, or copy any part of the Service except as expressly permitted.</li>
          <li>Use automated scrapers, bots, or load tests without prior written permission.</li>
          <li>Resell, sublicense, or white-label the Service without a separate written agreement.</li>
        </Ul>

        <H2>5. Plans, payment &amp; refunds</H2>
        <Ul>
          <li>HeDesk is offered on a 14-day free trial (no card required), then on a monthly subscription billed in advance.</li>
          <li>Plans and prices are listed at <a href="/#pricing" className="text-blue-400">hedesk.help/#pricing</a>. We may change pricing with at least 30 days&rsquo; notice to current subscribers.</li>
          <li>Payments are processed by Razorpay (India) and Stripe (international). We do not store full card details.</li>
          <li>Subscriptions auto-renew unless cancelled before the next billing cycle. Cancel any time from Settings.</li>
          <li><strong>Refunds:</strong> we offer a no-questions-asked 7-day refund on your first paid month. After that, monthly payments are non-refundable; annual payments are refunded pro-rata for unused months minus a 5% processing fee.</li>
        </Ul>

        <H2>6. Data ownership</H2>
        <P>
          <strong>Your data is yours.</strong> You retain all rights to clinic and patient data you enter. We
          process it only to operate the Service and as described in our{" "}
          <a href="/privacy" className="text-blue-400">Privacy Policy</a>. You may export the full archive at any time.
        </P>

        <H2>7. Our IP</H2>
        <P>
          The HeDesk software, name, logo, and design are owned by us. We grant you a non-exclusive,
          non-transferable, revocable licence to use the Service for the duration of your subscription.
        </P>

        <H2>8. Uptime &amp; support</H2>
        <Ul>
          <li>We target 99.5% uptime on the paid plans. Scheduled maintenance is announced 48 hours in advance.</li>
          <li>Support is provided via email at <a href="mailto:hello@hedesk.help" className="text-blue-400">hello@hedesk.help</a> with a response SLA of 1 business day on Pro and 4 hours on Enterprise.</li>
        </Ul>

        <H2>9. Termination</H2>
        <Ul>
          <li>You may cancel any time. On cancellation, you retain read-only access for 30 days, then your data is deleted (see Privacy Policy §6).</li>
          <li>We may suspend or terminate an account for breach of these Terms, with at least 7 days&rsquo; written notice except in cases of fraud, abuse, or legal compulsion.</li>
        </Ul>

        <H2>10. Liability</H2>
        <P>
          To the maximum extent permitted by Indian law, our total aggregate liability for any claim relating
          to the Service is capped at the total amount you have paid HeDesk in the 12 months preceding the
          claim. We are not liable for clinical outcomes, lost profits, or indirect damages.
        </P>

        <H2>11. Indemnity</H2>
        <P>
          You agree to indemnify HeDesk against any third-party claim arising from your use of the Service
          in breach of these Terms, your violation of applicable law, or your handling of patient data.
        </P>

        <H2>12. Governing law &amp; disputes</H2>
        <P>
          These Terms are governed by the laws of India. Disputes will be resolved exclusively in the courts
          of <strong>Thoubal, Manipur - 795138</strong>.
        </P>

        <H2>13. Changes to these terms</H2>
        <P>
          We may revise these Terms from time to time. Material changes will be notified to your primary
          contact email at least 14 days before they take effect.
        </P>

        <H2>14. Contact</H2>
        <P>
          <a href="mailto:hedesk.app@gmail.com" className="text-blue-400">hello@hedesk.help</a>
        </P>
      </article>
    </MarketingShell>
  );
}

function Lead({ children }) {
  return <p className="mt-8 text-lg text-slate-200 leading-relaxed border-l-2 border-blue-500/40 pl-4">{children}</p>;
}
function H2({ children }) {
  return (
    <h2 className="mt-12 mb-3 text-xl font-semibold text-white tracking-tight" style={{ fontFamily: "Manrope" }}>
      {children}
    </h2>
  );
}
function P({ children }) {
  return <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-4">{children}</p>;
}
function Ul({ children }) {
  return <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-300 leading-relaxed mb-5 marker:text-blue-400">{children}</ul>;
}
